/*
 * Custom hook that encapsulates all state, queries, effects and action handlers
 * for the ProductList page, keeping the component file focused on rendering.
 */

import React, {useEffect, useState, useRef, useCallback, useMemo} from 'react'
import {useHistory, useLocation, useParams} from 'react-router-dom'
import {useIntl} from 'react-intl'
import {
    useCategory,
    useCustomerId,
    useProductSearch,
    useShopperCustomersMutation
} from '@salesforce/commerce-sdk-react'
import {useProductSearchSSR} from '../../hooks/use-product-search-ssr'
import {useServerContext} from '@salesforce/pwa-kit-react-sdk/ssr/universal/hooks'
import {useHeaderContext} from '../../contexts/header-context'
import {getConfig} from '@salesforce/pwa-kit-runtime/utils/ssr-config'
import {useScript} from '../../contexts/script-context'
import {useBrand} from '../../contexts/brand-context'
import {
    Link,
    useBreakpointValue,
    useDisclosure
} from '@salesforce/retail-react-app/app/components/shared/ui'
import {useLimitUrls, useSearchParams} from '@salesforce/retail-react-app/app/hooks'
import {useSortUrls} from '../../hooks/use-sort-urls'
import useActiveData from '@salesforce/retail-react-app/app/hooks/use-active-data'
import {useViewportTracking} from '../../hooks/use-viewport-tracking'
import {useCustomToast} from '../../contexts/custom-toast-context'
import {useWishList} from '@salesforce/retail-react-app/app/hooks/use-wish-list'
import {useAppOrigin} from '@salesforce/retail-react-app/app/hooks/use-app-origin'
import {useOptimizelyPage} from '../../hooks/use-optimizely-page'
import {useClientCategoryId} from '../../hooks/use-client-category-id'
import {categoryUrlBuilder} from '../../utils/url'
import {isHydrated} from '@salesforce/retail-react-app/app/utils/utils'
import {GRID_VIEW} from './partials/grid-view-selector'
import {API_ERROR_MESSAGE, MAX_CACHE_AGE, STALE_WHILE_REVALIDATE} from '@salesforce/retail-react-app/app/constants'
import useNavigation from '@salesforce/retail-react-app/app/hooks/use-navigation'
import useMultiSite from '@salesforce/retail-react-app/app/hooks/use-multi-site'
import {getSiteEnvVarValue} from '../../utils/site-config'
import {
    sendViewItemListEvent,
    sendViewSearchResultsEvent,
    sendSelectItemEvent,
    sendWishlistEvent,
    setTempRecommendation
} from '../../utils/gtm-utils'

const REFINEMENT_DISALLOW_LIST = ['c_isNew']
const ROOT_CATEGORY_ID = 'root'

const selectMostSpecificPLPWrappers = (exactWrappers, inheritedWrappers, rootWrappers) => {
    if (exactWrappers.length > 0) return exactWrappers
    if (inheritedWrappers.length > 0) return inheritedWrappers
    return rootWrappers
}

export const useProductList = () => {
    const {enableScript, disableScript} = useScript()
    const {formatMessage} = useIntl()
    const navigate = useNavigation()
    const history = useHistory()
    const params = useParams()
    const location = useLocation()
    const {site, locale: currentLocale, buildUrl} = useMultiSite()
    const activeData = useActiveData()
    const {res} = useServerContext()
    const customerId = useCustomerId()
    const [searchParams, {stringify: stringifySearchParams}] = useSearchParams()
    const {showCustomToast} = useCustomToast()
    const config = getConfig()
    const siteId = site?.id
    const IS_NOIBU_DISABLED_PLP = getSiteEnvVarValue(config.app, 'IS_NOIBU_DISABLED_PLP', siteId)
    const {brandId} = useBrand()
    const {onOpen} = useDisclosure()
    const appOrigin = useAppOrigin()
    const {setCategory, setPageType} = useHeaderContext()

    // Get SEO data from server context for SSR
    const serverContext = useServerContext()
    const serverSeoData = serverContext?.req?.seoData || null

    const urlCategoryPath = params.categoryPath || params.categoryId || ''

    /**************** State ****************/
    const [seoData, setSeoData] = useState(() => {
        if (typeof window === 'undefined') return serverSeoData
        const windowSeoData = window.__SEO_DATA__
        if (windowSeoData && windowSeoData.categoryPath !== urlCategoryPath) return null
        return windowSeoData || null
    })

    const urlParams = new URLSearchParams(location.search)
    const isSmartSearchParam = urlParams.get('smartSearch') === 'true'
    const initialPage = parseInt(urlParams.get('page')) || 1
    const initialOffset = parseInt(urlParams.get('offset')) || 0

    // Even-page URLs are a desktop artifact (desktop pagination lands on even pages). When
    // landing/refreshing mid-group, seed prepend skeletons for the missing lower page(s) in
    // the INITIAL render (SSR + first client paint) so the user sees placeholders directly
    // instead of the loaded page alone (then a flash of the "Show Previous" button). Desktop
    // fills these via the auto extend-down; a real mobile landing clears them (see effect).
    const initialMobileLimit =
        Number(getSiteEnvVarValue(config.app, 'PRODUCTS_PER_PAGE_MOBILE', siteId)) || 36
    const initialDesktopGroupSize = Math.max(
        1,
        Math.round(
            (Number(getSiteEnvVarValue(config.app, 'PRODUCTS_PER_PAGE_DESKTOP', siteId)) || 72) /
                initialMobileLimit
        )
    )
    const initialMissingLowerPages = (initialPage - 1) % initialDesktopGroupSize

    const [filtersLoading, setFiltersLoading] = useState(false)
    const [wishlistLoading, setWishlistLoading] = useState([])
    const [sortOpen, setSortOpen] = useState(false)
    const [page, setPage] = useState(initialPage)
    const [minPageLoaded, setMinPageLoaded] = useState(initialPage)
    const [maxPageLoaded, setMaxPageLoaded] = useState(initialPage)
    const [skeletonPlaceholders, setSkeletonPlaceholders] = useState(() =>
        initialMissingLowerPages > 0
            ? Array.from({length: initialMissingLowerPages * initialMobileLimit}, (_, i) => ({
                  // Deterministic ids (no crypto.randomUUID) so SSR and client keys match.
                  id: `skeleton-prepend-seed-${i}`,
                  type: 'skeleton',
                  position: 'prepend'
              }))
            : []
    )
    const [isLoadingNewPage, setIsLoadingNewPage] = useState(false)
    // Gates the very first desktop auto-extend (page 1 -> page 2) behind a small scroll, so
    // page 2 doesn't load with zero interaction. Latches true once past the threshold.
    const [hasScrolledForExtend, setHasScrolledForExtend] = useState(false)
    // Flips true once we confirm a real mobile view (client matchMedia). Kept out of the
    // initial render so SSR/first paint stay desktop-assumed (consistent with the seed).
    const [confirmedMobile, setConfirmedMobile] = useState(false)
    const [isInitialLoad, setIsInitialLoad] = useState(true)
    const [currentPath, setCurrentPath] = useState(location.pathname)
    const [gridView, setGridView] = useState(
        () =>
            (typeof window !== 'undefined' && localStorage.getItem('plpGridView')) ||
            GRID_VIEW.DEFAULT
    )
    const [offset, setOffset] = useState(initialOffset)
    const [allProducts, setAllProducts] = useState([])
    const [accumulatedEnrichment, setAccumulatedEnrichment] = useState(new Map())

    const productRefs = useRef({})
    const productPageMap = useRef({})
    const prependScrollAnchor = useRef(null)
    // Latches a scroll-restore once we arrive via the browser back/forward button,
    // so the multi-step restore survives the history.replace goToPage does mid-restore.
    const restoreActiveRef = useRef(false)
    // Latches "this back-nav has no product to restore -> land at the top once the
    // view finishes assembling." While set, prepends skip the viewport-preserving
    // anchor so the auto group-completion doesn't pin us at the page boundary.
    const landAtTopRef = useRef(false)
    // Marks that the CURRENT prepend chain was started by a user click ("Show Previous").
    // Only a user chain keeps the viewport anchored; an AUTOMATIC group-completion extend-
    // down (e.g. fresh/refresh load of an even page prepending page 1 above page 2) must
    // not anchor, so it fills upward and leaves the user at the top.
    const userPrependRef = useRef(false)
    // How many lower pages were pre-seeded as prepend skeletons in the initial render. The
    // auto extend-down consumes these (one per prepend) so goToPage doesn't create a second
    // set; a mobile landing clears them instead.
    const seededPrependPagesRef = useRef(initialMissingLowerPages)

    // Deferred so it wins over the browser's native scroll restoration / scroll anchoring.
    const scrollWindowToTop = useCallback(() => {
        if (typeof window === 'undefined') return
        queueMicrotask(() =>
            requestAnimationFrame(() => window.scrollTo({top: 0, behavior: 'instant'}))
        )
    }, [])

    /**************** Derived from params ****************/
    const currentCategoryPath = params.categoryPath || params.categoryId || ''
    const lastCategoryPathRef = useRef(currentCategoryPath)

    const hasSEOUrlParams = params.brandId && params.categoryPath
    const localeAlias = currentLocale?.alias || currentLocale?.id
    const {data: clientCategoryId, error: clientCategoryError} = useClientCategoryId(
        localeAlias,
        params.brandId,
        params.categoryPath,
        false
    )

    const cgidFromQuery = searchParams.cgid
    const effectiveCategoryId = useMemo(() => {
        if (hasSEOUrlParams) {
            return seoData?.resolvedCategoryId || clientCategoryId
        }
        return params.categoryId || cgidFromQuery
    }, [hasSEOUrlParams, seoData, clientCategoryId, params.categoryId, cgidFromQuery])

    const serverSeoNotFound = Boolean(serverSeoData?.seoNotFound || seoData?.seoNotFound)
    const clientSeoNotFound =
        hasSEOUrlParams &&
        !serverSeoNotFound &&
        !!clientCategoryError &&
        (clientCategoryError?.message?.includes('404') || false)

    const isAlternativeGridView = gridView === GRID_VIEW.ALTERNATIVE
    const gridColumns = isAlternativeGridView ? [3, 3, 6, 6] : [2, 2, 4, 4]

    let searchQuery = urlParams.get('q')
    const isSearch = !!searchQuery

    /**************** CMS / Optimizely ****************/
    // The PLP CMS layout lives on a single shared GDIPLPPage. Each block inside
    // (AboveProductListArea / BelowProductListArea / GridInterruptors) self-targets
    // a category via CategoryId + ApplyToSubcategories.
    const {data: categoryContentData, isLoading: isCategoryContentLoading} =
        useOptimizelyPage('plp')

    /**************** CLP detection ****************/
    // Parallel SSR fetch: if Optimizely has authored a page under the active
    // brand/locale tree with `categoryId` as its RouteSegment, the page is
    // rendered as a full CLP (no product grid). Runs in parallel with the
    // product search + 'plp' decorations so PLP SSR latency is unchanged.
    const {data: clpData, isLoading: isClpLoading} = useOptimizelyPage(effectiveCategoryId || null)
    const clpPage = clpData?.page || clpData
    const isClpMode = !!(clpPage?.items?.length || clpPage?.Content?.items?.length)

    /**************** Search no-results Optimizely page ****************/
    // Fetched only on search pages (isSearch). When the CMS author creates a page
    // with SimpleAddress / RouteSegment = 'search-no-results' it will be rendered
    // instead of the default EmptySearchResults component.
    const {data: searchNoResultsData, isLoading: isSearchNoResultsLoading} = useOptimizelyPage(
        isSearch ? 'search-no-results' : null
    )
    const searchNoResultsPage = searchNoResultsData?.page || searchNoResultsData
    const hasSearchNoResultsPage = !!(
        searchNoResultsPage?.items?.length || searchNoResultsPage?.Content?.items?.length
    )

    const currentBreakpoint = useBreakpointValue({
        base: 'mobile',
        md: 'tablet',
        lg: 'desktop',
        xl: 'xl'
    })

    // Client-synchronous desktop check (reads matchMedia on the first client render rather
    // than the SSR 'base' fallback). Used only in effects — never in rendered output — to
    // avoid hydration mismatches, e.g. to clear desktop-assumed seeded skeletons on mobile.
    // ssr:true on the server (no window access); ssr:false on the client for a synchronous
    // matchMedia read from the first client render.
    const isDesktopClient = useBreakpointValue(
        {base: false, lg: true},
        {ssr: typeof window === 'undefined', fallback: 'base'}
    )

    /**************** Search Params Setup ****************/
    if (effectiveCategoryId) {
        searchParams._refine.push(`cgid=${effectiveCategoryId}`)
    }
    if (!effectiveCategoryId && getSiteEnvVarValue(config.app, 'ROOT_CATEGORY_ID_GARAGE', siteId)) {
        searchParams._refine.push(`cgid=${getSiteEnvVarValue(config.app, 'ROOT_CATEGORY_ID_GARAGE', siteId)}`)
    }
    const isDesktopBreakpoint = currentBreakpoint === 'desktop' || currentBreakpoint === 'xl'

    // One universal page size (36) for both breakpoints, so the URL `page` means
    // the same everywhere. Desktop loads in groups of `desktopGroupSize` pages
    // (PRODUCTS_PER_PAGE_DESKTOP / PRODUCTS_PER_PAGE_MOBILE = 72/36 = 2), so a
    // "view" is 72 products ending on an even page. Mobile loads one page at a time.
    const fullDesktopLimit = Number(getSiteEnvVarValue(config.app, 'PRODUCTS_PER_PAGE_DESKTOP', siteId)) || 72
    const mobileLimit = Number(getSiteEnvVarValue(config.app, 'PRODUCTS_PER_PAGE_MOBILE', siteId)) || 36
    const pageSize = mobileLimit
    const desktopGroupSize = isDesktopBreakpoint
        ? Math.max(1, Math.round(fullDesktopLimit / mobileLimit))
        : 1

    const getLimitForPage = useCallback(() => pageSize, [pageSize])

    const getOffsetForPage = useCallback((p) => (p - 1) * pageSize, [pageSize])

    searchParams.limit = getLimitForPage(page)
    searchParams.offset = getOffsetForPage(page)
    if (searchQuery && searchParams.q !== searchQuery) {
        searchParams.q = searchQuery
    }

    const {_refine, ...restOfParams} = searchParams
    const limit = searchParams.limit
    const isProductSearchEnabled = !!(effectiveCategoryId || searchQuery)

    /**************** Mutations ****************/
    const {mutateAsync: createCustomerProductListItem} = useShopperCustomersMutation(
        'createCustomerProductListItem'
    )
    const {mutateAsync: deleteCustomerProductListItem} = useShopperCustomersMutation(
        'deleteCustomerProductListItem'
    )

    /**************** Category change tracking ****************/
    const [lastEffectiveCategoryId, setLastEffectiveCategoryId] = useState(effectiveCategoryId)
    const [isCategoryChanging, setIsCategoryChanging] = useState(false)
    const [lastRefineKey, setLastRefineKey] = useState(() =>
        JSON.stringify(searchParams.refine) + '|' + searchParams.sort
    )

    /**************** Product Search – Phase 1 (SSR + fast hydration) ****************/
    const {
        isLoading,
        isFetched,
        isRefetching,
        data: productSearchResult
    } = useProductSearchSSR(
        {
            parameters: {
                ...restOfParams,
                ...(effectiveCategoryId && !searchQuery ? {categoryId: effectiveCategoryId} : {}),
                ...(isSmartSearchParam ? {smartSearch: 'true'} : {}),
                perPricebook: true,
                expand: ['prices', 'images', 'custom_properties', 'page_meta_tags'],
                refine: _refine
            }
        },
        {
            keepPreviousData: !isCategoryChanging,
            enabled: isProductSearchEnabled,
            staleTime: 60 * 1000
        }
    )

    /**************** Product Search – Phase 2 (client enrichment) ****************/
    const isClient = typeof window !== 'undefined'

    const {data: enrichmentSearchResult} = useProductSearch(
        {
            parameters: {
                ...restOfParams,
                ...(effectiveCategoryId && !searchQuery ? {categoryId: effectiveCategoryId} : {}),
                perPricebook: true,
                allImages: true,
                allVariationProperties: true,
                expand: [
                    'prices',
                    'images',
                    'promotions',
                    'variations',
                    'custom_properties',
                    'page_meta_tags'
                ],
                refine: _refine
            }
        },
        {
            enabled: isClient && isProductSearchEnabled && !isSmartSearchParam,
            staleTime: 15 * 60 * 1000
        }
    )

    /**************** Category Query ****************/
    const {error, data: category} = useCategory(
        {
            parameters: {
                id: effectiveCategoryId,
                ...(brandId ? {c_brand: brandId} : {})
            }
        },
        {enabled: !isSearch && !!effectiveCategoryId}
    )

    /**************** Wishlist ****************/
    const {data: wishlist} = useWishList()

    /**************** Sort / Limit URLs ****************/
    const {total, sortingOptions} = productSearchResult || {}
    const sortUrls = useSortUrls({options: sortingOptions})
    const limitUrls = useLimitUrls()

    /**************** Enrichment ****************/
    const enrichmentMap = useMemo(() => {
        const map = new Map(accumulatedEnrichment)
        const hits = enrichmentSearchResult?.hits || []
        hits.forEach((hit) => map.set(hit.productId, hit))
        return map
    }, [accumulatedEnrichment, enrichmentSearchResult])

    const enrichProduct = useCallback(
        (product) => {
            const enrichment = enrichmentMap.get(product.productId)
            if (!enrichment) return product
            return {
                ...product,
                productPromotions: enrichment.productPromotions,
                variants: enrichment.variants,
                variationGroups: enrichment.variationGroups,
                variationAttributes: enrichment.variationAttributes,
                imageGroups: enrichment.imageGroups,
                ...enrichment
            }
        },
        [enrichmentMap]
    )

    /**************** GA4 Viewport Tracking ****************/
    const currentProducts = isHydrated() ? allProducts : productSearchResult?.hits || []

    const stableOnViewItemList = useCallback(
        (products) => {
            if (isSearch) {
                sendViewSearchResultsEvent(searchQuery, products)
            } else {
                sendViewItemListEvent(category, products, false)
            }
        },
        [isSearch, searchQuery, category]
    )

    const {observeProduct} = useViewportTracking({
        products: currentProducts,
        categoryId: effectiveCategoryId,
        searchQuery,
        visibilityThreshold: 0.5,
        onViewItemList: stableOnViewItemList
    })

    /**************** Refinements disallow list ****************/
    if (productSearchResult?.refinements) {
        productSearchResult.refinements = productSearchResult.refinements.filter(
            ({attributeId}) => !REFINEMENT_DISALLOW_LIST.includes(attributeId)
        )
    }

    /**************** Cache-Control header ****************/
    if (res) {
        res.set(
            'Cache-Control',
            `s-maxage=${MAX_CACHE_AGE}, stale-while-revalidate=${STALE_WHILE_REVALIDATE}`
        )
    }

    /**************** Derived render state ****************/
    // Guard against showing "no results" when accumulated products already exist.
    // This prevents a blank grid when a stale or out-of-range page fetch returns
    // null hits while allProducts still holds the correctly-restored page data
    // (e.g. after back-navigation triggers a spurious goToPage call).
    const showNoResults =
        !isLoading && productSearchResult && !productSearchResult?.hits && allProducts.length === 0
    const isSmartSearch = productSearchResult?.isSmartSearch
    const basePath = `${location.pathname}${location.search}`
    const hasSelectedFilters =
        productSearchResult?.selectedRefinements !== undefined &&
        Object.keys(productSearchResult.selectedRefinements).filter(
            (key) => key !== 'q' && key !== 'cgid'
        ).length > 0
    const pageTitle =
        productSearchResult?.pageMetaTags?.find((option) => option.id === 'title')?.value || ''
    const initialLabel =
        productSearchResult?.sortingOptions?.find(
            (option) => option.id === productSearchResult?.selectedSortingOption
        )?.label || ''
    const [selectedLabel, setSelectedLabel] = useState(initialLabel)

    /**************** PLP CMS section helpers ****************/
    // A block "matches" the current category in one of three ways:
    //   - root: CategoryId === 'root' and acts as the global PLP fallback
    //   - exact: its CategoryId equals the active category id
    //   - inherited: ApplyToSubcategories is true AND the active category
    //     is a descendant (CategoryId appears in `parentCategoryTree`)
    // Inherited content from a parent category is suppressed whenever the
    // current category configures its own block in the same area, so
    // child-specific content always overrides parent fallbacks; inherited
    // content likewise overrides root fallback content.
    const classifyPLPCategoryMatch = useCallback(
        (block) => {
            const blockCategoryId = block?.CategoryId
            if (!blockCategoryId) return 'none'
            if (blockCategoryId === ROOT_CATEGORY_ID) return 'root'
            if (
                blockCategoryId === effectiveCategoryId ||
                blockCategoryId === category?.id
            ) {
                return 'exact'
            }
            if (block.ApplyToSubcategories) {
                const tree = category?.parentCategoryTree || []
                if (tree.some((c) => c?.id === blockCategoryId)) return 'inherited'
            }
            return 'none'
        },
        [effectiveCategoryId, category]
    )

    // Flatten a `GDIPLPPageProductListAdjacentBlock` area into the renderable
    // GDI children of every wrapper that targets the current category. Exact
    // wrappers beat inherited wrappers, which beat root fallback wrappers.
    const extractAdjacentBlocks = useCallback(
        (areaItems) => {
            if (!Array.isArray(areaItems)) return []
            const exactWrappers = []
            const inheritedWrappers = []
            const rootWrappers = []
            areaItems.forEach((wrapper) => {
                const expanded = wrapper?.ContentLink?.Expanded
                if (expanded?.__typename !== 'GDIPLPPageProductListAdjacentBlock') return
                const match = classifyPLPCategoryMatch(expanded)
                if (match === 'exact') exactWrappers.push(expanded)
                else if (match === 'inherited') inheritedWrappers.push(expanded)
                else if (match === 'root') rootWrappers.push(expanded)
            })
            const wrappersToUse = selectMostSpecificPLPWrappers(
                exactWrappers,
                inheritedWrappers,
                rootWrappers
            )
            const blocks = []
            wrappersToUse.forEach((expanded) => {
                const children = expanded.GDIPLPPageProductListAdjacentBlockContent || []
                children.forEach((child) => {
                    const childExpanded = child?.ContentLink?.Expanded
                    if (childExpanded) blocks.push(childExpanded)
                })
            })
            return blocks
        },
        [classifyPLPCategoryMatch]
    )

    const aboveProductListBlocks = useMemo(
        () => extractAdjacentBlocks(categoryContentData?.item?.AboveProductListArea),
        [categoryContentData, extractAdjacentBlocks]
    )

    const belowProductListBlocks = useMemo(
        () => extractAdjacentBlocks(categoryContentData?.item?.BelowProductListArea),
        [categoryContentData, extractAdjacentBlocks]
    )

    const getCategoryNavigationPath = () => {
        const categoryPath = category ? categoryUrlBuilder(category) : null
        return categoryPath ? buildUrl(categoryPath) : location.pathname
    }

    /**************** Skeleton helpers ****************/
    const createSkeletonPlaceholders = (count, position = 'append') => {
        const skeletons = Array.from({length: count}, (_, index) => ({
            id: `skeleton-${position}-${crypto.randomUUID()}-${index}`,
            type: 'skeleton',
            position
        }))
        setSkeletonPlaceholders((prev) =>
            position === 'prepend' ? [...skeletons, ...prev] : [...prev, ...skeletons]
        )
    }

    const removeSkeletonPlaceholders = (position) => {
        setSkeletonPlaceholders((prev) => prev.filter((s) => s.position !== position))
    }

    /**************** Grid interruptor positions ****************/
    // Map of `{ position: GDIBlock[] }` for the current breakpoint, derived from
    // GridInterruptors blocks that target the active category. Each interruptor
    // wrapper can carry several GDI children; they all render at its slot.
    // If the current category configures its own GridInterruptors wrapper,
    // inherited (parent + ApplyToSubcategories) wrappers are dropped so child
    // content overrides the parent fallback. Root wrappers act as the final
    // fallback.
    const gridInterruptorPositions = useMemo(() => {
        const positions = {}
        const items = categoryContentData?.item?.GridInterruptors
        if (!Array.isArray(items)) return positions
        const exactWrappers = []
        const inheritedWrappers = []
        const rootWrappers = []
        items.forEach((wrapper) => {
            const expanded = wrapper?.ContentLink?.Expanded
            if (expanded?.__typename !== 'GDIPLPPageGridInterruptorsBlock') return
            const match = classifyPLPCategoryMatch(expanded)
            if (match === 'exact') exactWrappers.push(expanded)
            else if (match === 'inherited') inheritedWrappers.push(expanded)
            else if (match === 'root') rootWrappers.push(expanded)
        })
        const wrappersToUse = selectMostSpecificPLPWrappers(
            exactWrappers,
            inheritedWrappers,
            rootWrappers
        )
        wrappersToUse.forEach((expanded) => {
            // Positions cascade smaller -> larger breakpoint. If the current
            // breakpoint has no value, fall back to the nearest smaller one
            // that does (mobile -> tablet -> desktop -> xl).
            const breakpointOrder = ['mobile', 'tablet', 'desktop', 'xl']
            const positionByBreakpoint = {
                mobile: expanded.PositionMobile,
                tablet: expanded.PositionTablet,
                desktop: expanded.PositionDesktop,
                xl: expanded.PositionXL
            }
            const currentIndex = breakpointOrder.indexOf(currentBreakpoint)
            let position = null
            for (let i = currentIndex; i >= 0; i--) {
                const value = positionByBreakpoint[breakpointOrder[i]]
                if (value != null && value !== '') {
                    position = value
                    break
                }
            }
            if (!position) return
            const children = expanded.GDIPLPPageGridInterruptorsBlockContent || []
            children.forEach((child) => {
                const childExpanded = child?.ContentLink?.Expanded
                if (!childExpanded) return
                if (!positions[position]) positions[position] = []
                positions[position].push(childExpanded)
            })
        })
        return positions
    }, [categoryContentData, currentBreakpoint, classifyPLPCategoryMatch])

    /**************** Grid view persistence ****************/
    const setGridViewPersisted = (view) => {
        localStorage.setItem('plpGridView', view)
        setGridView(view)
    }

    /**************** Effects ****************/
    // Clear seoData when category path changes
    useEffect(() => {
        if (currentCategoryPath !== lastCategoryPathRef.current && seoData) {
            setSeoData(null)
        }
        lastCategoryPathRef.current = currentCategoryPath
    }, [currentCategoryPath, seoData, location.pathname])

    // Noibu script toggle
    useEffect(() => {
        if (IS_NOIBU_DISABLED_PLP === 'true') {
            disableScript('noibu')
        } else {
            enableScript('noibu')
        }
        return () => {
            if (IS_NOIBU_DISABLED_PLP) enableScript('noibu')
        }
    }, [])

    // Reset on location change (new page navigation)
    useEffect(() => {
        const hasQueryParams = location.search.length > 0
        const isFirstPage = !new URLSearchParams(location.search).has('page')
        if (!hasQueryParams || isFirstPage) {
            setPage(1)
            setOffset(0)
            setAllProducts([])
            setMinPageLoaded(1)
            setMaxPageLoaded(1)
            setSkeletonPlaceholders([])
            setIsLoadingNewPage(false)
            // Re-gate the first page-2 auto-extend behind a fresh scroll on the new view.
            setHasScrolledForExtend(false)
            if (productPageMap?.current) productPageMap.current = {}
        }
    }, [location.pathname, location.search])

    // Category change detection
    useEffect(() => {
        if (effectiveCategoryId && effectiveCategoryId !== lastEffectiveCategoryId) {
            // Only a GENUINE switch from a previously-known category resets to page 1 and
            // strips carried page/offset. The initial async resolution of effectiveCategoryId
            // (undefined -> value of the SAME category — e.g. a back-nav remount on a SEO URL
            // where the id resolves via seoData/clientCategoryId) must NOT reset or strip,
            // or it would drop the restored ?page=4 and send us to the no-param URL.
            if (lastEffectiveCategoryId != null) {
                setIsCategoryChanging(true)
                // A new category always starts at page 1 — never carry the previous PLP's
                // page/offset over. Carrying them loads mid-catalog and, on desktop, triggers
                // a backwards extend-down that writes ?page=1 and lands the user mid-page.
                setAllProducts([])
                setPage(1)
                setOffset(0)
                setMinPageLoaded(1)
                setMaxPageLoaded(1)
                setSkeletonPlaceholders([])
                setIsLoadingNewPage(false)
                // Re-gate the first page-2 auto-extend behind a fresh scroll on the new category.
                setHasScrolledForExtend(false)
                if (productPageMap?.current) productPageMap.current = {}
                // Drop any stale page/offset carried in from the previous category's URL.
                const qp = new URLSearchParams(location.search)
                if (qp.has('page') || qp.has('offset')) {
                    qp.delete('page')
                    qp.delete('offset')
                    const qs = qp.toString()
                    history.replace(`${location.pathname}${qs ? `?${qs}` : ''}`)
                }
            }
            setLastEffectiveCategoryId(effectiveCategoryId)
        }
    }, [effectiveCategoryId, lastEffectiveCategoryId, location.search])

    // Filter/sort change reset
    useEffect(() => {
        const currentRefineKey = JSON.stringify(searchParams.refine) + '|' + searchParams.sort
        if (currentRefineKey !== lastRefineKey) {
            setAllProducts([])
            setPage(1)
            setOffset(0)
            setMinPageLoaded(1)
            setMaxPageLoaded(1)
            setSkeletonPlaceholders([])
            setIsLoadingNewPage(false)
            // Re-gate the first page-2 auto-extend behind a fresh scroll after a filter change.
            setHasScrolledForExtend(false)
            if (productPageMap?.current) productPageMap.current = {}
            setLastRefineKey(currentRefineKey)
        }
    }, [searchParams.refine, searchParams.sort, lastRefineKey])

    // Accumulate enrichment across pages
    useEffect(() => {
        const hits = enrichmentSearchResult?.hits || []
        if (hits.length > 0) {
            setAccumulatedEnrichment((prev) => {
                const next = new Map(prev)
                hits.forEach((hit) => next.set(hit.productId, hit))
                return next
            })
        }
    }, [enrichmentSearchResult])

    // Clear enrichment on category change
    useEffect(() => {
        if (isCategoryChanging) setAccumulatedEnrichment(new Map())
    }, [isCategoryChanging])

    // Clear enrichment on filter change
    useEffect(() => {
        setAccumulatedEnrichment(new Map())
    }, [lastRefineKey])

    // Reset category changing flag when new products arrive
    useEffect(() => {
        if (productSearchResult?.hits?.length > 0 && isCategoryChanging) {
            setIsCategoryChanging(false)
        }
    }, [productSearchResult, isCategoryChanging])

    // Accumulate product pages
    useEffect(() => {
        if (productSearchResult?.hits?.length) {
            setAllProducts((prev) => {
                const ids = new Set(prev.map((p) => p.productId))
                const newProducts = productSearchResult.hits.filter((p) => !ids.has(p.productId))
                if (newProducts.length === 0) return prev
                if (page < minPageLoaded) removeSkeletonPlaceholders('prepend')
                else if (page > maxPageLoaded) removeSkeletonPlaceholders('append')
                setIsLoadingNewPage(false)
                if (prev.length === 0) return newProducts
                if (page < minPageLoaded) return [...newProducts, ...prev]
                if (page > maxPageLoaded) return [...prev, ...newProducts]
                return prev
            })
            productSearchResult.hits.forEach((p) => {
                productPageMap.current[p.productId] = page
            })
            setMinPageLoaded((prev) => Math.min(prev, page))
            setMaxPageLoaded((prev) => Math.max(prev, page))
            const newLabel =
                productSearchResult?.sortingOptions?.find(
                    (option) => option.id === productSearchResult?.selectedSortingOption
                )?.label || ''
            setSelectedLabel(newLabel)
        }
    }, [productSearchResult])

    // Arm a scroll restore ONCE per navigation, only on a browser back/forward (POP).
    // history.action lingers as 'POP' after a back-nav AND on a fresh/direct page load
    // until the next push/replace — and "Show More" (to an odd page) / "Show Previous"
    // never replace the URL, so the action stays 'POP'. Reading it on every render
    // re-armed the restore and yanked the user to the top on those interactions. Keying
    // off location.key arms exactly once when the navigation actually happens.
    useEffect(() => {
        if (history.action === 'POP') restoreActiveRef.current = true
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [location.key])

    // Scroll back to product only when a restore was armed by a back/forward POP.
    // Normal nav (product click, site nav) uses push/replace and must not scroll.
    // The arm latch survives goToPage's replace mid-restore until a terminal decision.
    useEffect(() => {
        if (!restoreActiveRef.current) return

        let productContext = null
        try {
            productContext = JSON.parse(sessionStorage.getItem('plp-productContext'))
        } catch {
            productContext = null
        }
        // No product to restore -> start the PLP back at the top once it assembles.
        if (!productContext?.productId) {
            restoreActiveRef.current = false
            landAtTopRef.current = true
            scrollWindowToTop()
            return
        }

        // Wait until data has settled AND (on desktop) the page group has finished
        // assembling before deciding. We intentionally do NOT navigate by page number
        // here — the clicked product's page is already restored from the URL, and the
        // group auto-completes; navigating would fight that extend and stall the fetch.
        const hasProducts = allProducts.length > 0 || productSearchResult?.hits?.length > 0
        if (isLoading || isLoadingNewPage || !isFetched || !hasProducts) return
        // Only the content ABOVE the clicked product must be settled (lower pages aligned);
        // pages loading below don't shift it. This also lets a page-1 product restore without
        // waiting for the now scroll-gated page 2.
        const groupSettled = desktopGroupSize <= 1 || (minPageLoaded - 1) % desktopGroupSize === 0
        if (!groupSettled) return

        const ref = productRefs.current[productContext.productId]
        if (ref) {
            const timeoutId = setTimeout(() => {
                productRefs.current[productContext.productId]?.scrollIntoView({
                    behavior: 'smooth',
                    block: 'center'
                })
                sessionStorage.removeItem('plp-productContext')
                restoreActiveRef.current = false
            }, 150)
            return () => clearTimeout(timeoutId)
        }
        // Group is assembled but the clicked product isn't on it -> top.
        sessionStorage.removeItem('plp-productContext')
        restoreActiveRef.current = false
        landAtTopRef.current = true
        scrollWindowToTop()
    }, [
        page,
        isLoading,
        isLoadingNewPage,
        isFetched,
        allProducts,
        productSearchResult,
        minPageLoaded,
        maxPageLoaded,
        desktopGroupSize,
        enrichmentMap
    ])

    // While a "land at top" back-nav is in flight, re-assert scroll-to-top once the
    // desktop group has fully assembled (the auto extend-down can prepend page 1 and
    // shift the viewport; this is the final word).
    useEffect(() => {
        if (!landAtTopRef.current) return
        if (isLoading || isLoadingNewPage) return
        // Only the content above matters for landing at the top; don't wait for the
        // scroll-gated page 2 below (else this would re-assert scroll-to-top after the
        // user scrolls down to trigger page 2).
        const groupSettled = desktopGroupSize <= 1 || (minPageLoaded - 1) % desktopGroupSize === 0
        if (!groupSettled) return
        landAtTopRef.current = false
        scrollWindowToTop()
    }, [isLoading, isLoadingNewPage, minPageLoaded, maxPageLoaded, desktopGroupSize, scrollWindowToTop])

    // A real mobile landing won't auto-extend-down to fill the desktop-assumed seeded
    // prepend skeletons, so clear them (the manual "Show Previous" button takes over).
    // isDesktopClient is resolved synchronously on the client (matchMedia), so it's reliable
    // from the first client render — unlike the SSR-fallback breakpoint above.
    useEffect(() => {
        if (isDesktopClient === false) {
            setConfirmedMobile(true)
            if (seededPrependPagesRef.current > 0) {
                seededPrependPagesRef.current = 0
                removeSkeletonPlaceholders('prepend')
            }
        }
    }, [isDesktopClient])

    // Latch hasScrolledForExtend once the user scrolls a little, so the first desktop
    // auto-extend (page 1 -> page 2) waits for interaction instead of firing instantly.
    useEffect(() => {
        if (hasScrolledForExtend || typeof window === 'undefined') return
        const SCROLL_TRIGGER_PX = 1000
        const onScroll = () => {
            if (window.scrollY >= SCROLL_TRIGGER_PX) setHasScrolledForExtend(true)
        }
        // Respond to real scroll events only — no immediate read of scrollY. On a category
        // nav the flag is reset to false and the listener re-attaches, but scrollTo(0) from
        // ScrollToTop may not have landed yet; reading stale scrollY here would wrongly
        // re-latch and auto-load page 2. ScrollToTop / scrollIntoView fire scroll events, so
        // genuine scrolled positions still latch.
        window.addEventListener('scroll', onScroll, {passive: true})
        return () => window.removeEventListener('scroll', onScroll)
    }, [hasScrolledForExtend])

    // Keep the loaded range aligned to whole desktop groups (e.g. pairs), filling
    // eagerly with no scroll. Covers initial load, "Show More", and back-nav.
    useEffect(() => {
        if (desktopGroupSize <= 1) return
        if (isLoading || isLoadingNewPage || isCategoryChanging || total == null) return
        // Extend up to the group end (initial 2nd page + Show More's partner page).
        if (
            maxPageLoaded % desktopGroupSize !== 0 &&
            getOffsetForPage(maxPageLoaded + 1) < total
        ) {
            // The very first auto-extend (page 1 -> page 2) waits for a small scroll so page 2
            // doesn't load with zero interaction. Deep links to higher pages and "Show More"
            // completions (maxPageLoaded > 1) stay eager.
            if (maxPageLoaded === 1 && !hasScrolledForExtend) return
            goToPage(maxPageLoaded + 1)
            return
        }
        // Extend down to the group start (back-nav landing on an even page).
        if ((minPageLoaded - 1) % desktopGroupSize !== 0 && minPageLoaded > 1) {
            goToPage(minPageLoaded - 1)
        }
    }, [
        maxPageLoaded,
        minPageLoaded,
        isLoading,
        isLoadingNewPage,
        isCategoryChanging,
        total,
        desktopGroupSize,
        hasScrolledForExtend
    ])

    // Refetch → filters loading
    useEffect(() => {
        setFiltersLoading(isRefetching)
    }, [isRefetching])

    // Page type context
    useEffect(() => {
        setPageType('product-list')
        return () => setPageType(null)
    }, [setPageType])

    // Category context
    useEffect(() => {
        setCategory(category)
        return () => setCategory(null)
    }, [category])

    // Active Data analytics
    useEffect(() => {
        if (productSearchResult) {
            if (isSearch) {
                activeData.sendViewSearch(searchParams, productSearchResult)
            } else {
                activeData.sendViewCategory(searchParams, category, productSearchResult)
            }
        }
    }, [productSearchResult])

    // Initial load flag
    useEffect(() => {
        if (location.pathname !== currentPath) {
            setIsInitialLoad(true)
            setCurrentPath(location.pathname)
        }
    }, [location.pathname, currentPath])

    useEffect(() => {
        if (!isLoading && productSearchResult?.hits) setIsInitialLoad(false)
    }, [isLoading, productSearchResult])

    // Dispatch plp:ready when smart search results are available
    useEffect(() => {
        if (isFetched && (productSearchResult?.hits?.length > 0 || showNoResults)) {
            window.dispatchEvent(new CustomEvent('plp:ready'))
        }
    }, [isFetched, productSearchResult, showNoResults])

    /**************** Action Handlers ****************/
    const addItemToWishlist = async (product, index) => {
        setWishlistLoading([...wishlistLoading, product.productId])
        const listId = wishlist.id
        const isMaximumNumberOfItemsReached = wishlist?.customerProductListItems?.length >= 100
        const {isGarage} = useBrand()
        
        if (isMaximumNumberOfItemsReached) {
            showCustomToast({
                message: isGarage
                    ? formatMessage(
                          {
                              id: 'cart.info.full_message.garage',
                              defaultMessage:
                                  'You have reached the maximum number of items for your Wishlist. <a>See Wishlist</a>'
                          },
                          {
                              a: (chunks) => (
                                  <Link
                                      href="/account/wishlist"
                                      display="block"
                                      mt={2}
                                      textDecoration="underline"
                                  >
                                      {chunks}
                                  </Link>
                              )
                          }
                      )
                    : formatMessage(
                          {
                              id: 'cart.info.full_message.dynamite',
                              defaultMessage:
                                  'You have reached the maximum number of items for your Wishlist. <a>See Wishlist</a>'
                          },
                          {
                              a: (chunks) => (
                                  <Link
                                      href="/account/wishlist"
                                      display="block"
                                      mt={2}
                                      textDecoration="underline"
                                  >
                                      {chunks}
                                  </Link>
                              )
                          }
                      )
            })
        } else {
            await createCustomerProductListItem(
                {
                    parameters: {customerId, listId},
                    body: {quantity: 1, public: true, priority: 1, type: 'product', productId: product.productId}
                },
                {
                    onError: () => {
                        showCustomToast({
                            message: formatMessage(API_ERROR_MESSAGE)
                        })
                    },
                    onSuccess: () => {
                        showCustomToast({
                            message: formatMessage({id: 'cart.info.item_added', defaultMessage: 'Added to Wishlist'}),
                            actionLabel: formatMessage({id: 'cart.info.view_list', defaultMessage: 'View List'}),
                            onActionClick: () => navigate('/account/wishlist')
                        })
                        sendWishlistEvent(product, false, index)
                    },
                    onSettled: () => {
                        setWishlistLoading(wishlistLoading.filter((id) => id !== product.productId))
                    }
                }
            )
        }
    }

    const removeItemFromWishlist = async (product, index) => {
        setWishlistLoading([...wishlistLoading, product.productId])
        const listId = wishlist.id
        const itemId = wishlist.customerProductListItems.find(
            (i) => i.productId === product.productId
        ).id
        await deleteCustomerProductListItem(
            {body: {}, parameters: {customerId, listId, itemId}},
            {
                onError: () => {
                    showCustomToast({
                        message: formatMessage(API_ERROR_MESSAGE)
                    })
                },
                onSuccess: () => {
                    showCustomToast({
                        message: formatMessage({id: 'wishlist.info.item_removed', defaultMessage: 'Item removed'})
                    })
                    sendWishlistEvent(product, true, index)
                },
                onSettled: () => {
                    setWishlistLoading(wishlistLoading.filter((id) => id !== product.productId))
                }
            }
        )
    }

    const normalizeRefinementValue = (value) => {
        if (value === null || value === undefined) return ''

        const asString = String(value)
        const plusNormalized = asString.replace(/\+/g, ' ')

        try {
            return decodeURIComponent(plusNormalized)
        } catch {
            return plusNormalized
        }
    }

    const toggleFilter = (value, attributeId, selected, allowMultiple = true) => {
        const searchParamsCopy = {
            ...searchParams,
            refine: Array.isArray(searchParams.refine) ? {} : {...(searchParams.refine || {})}
        }
        delete searchParamsCopy.offset
        delete searchParamsCopy.page

        if (!allowMultiple) {
            const previousValue = searchParamsCopy.refine[attributeId]
            delete searchParamsCopy.refine[attributeId]
            if (!selected && value.value != previousValue) {
                searchParamsCopy.refine[attributeId] = value.value
            }
        } else {
            let attributeValue = searchParamsCopy.refine[attributeId] || []
            if (typeof attributeValue === 'string') attributeValue = attributeValue.split('|')
            else if (typeof attributeValue === 'number') attributeValue = [attributeValue]
            if (!selected) {
                attributeValue.push(value.value)
            } else {
                const targetValue = normalizeRefinementValue(value.value)
                attributeValue = attributeValue?.filter(
                    (v) => normalizeRefinementValue(v) !== targetValue
                )
            }
            searchParamsCopy.refine[attributeId] = attributeValue
            if (searchParamsCopy.refine[attributeId].length === 0) {
                delete searchParamsCopy.refine[attributeId]
            }
        }

        if (Object.keys(searchParamsCopy.refine).length === 0) {
            searchParamsCopy.refine = []
        }

        if (isSearch) {
            navigate(`/search?${stringifySearchParams(searchParamsCopy)}`, 'replace')
        } else {
            history.replace(
                `${getCategoryNavigationPath()}?${stringifySearchParams(searchParamsCopy)}`
            )
        }
    }

    const resetFilters = () => {
        const newSearchParams = {...searchParams, refine: []}
        delete newSearchParams.page
        delete newSearchParams.offset
        if (isSearch) {
            navigate(`/search?${stringifySearchParams(newSearchParams)}`, 'replace')
        } else {
            history.replace(
                `${getCategoryNavigationPath()}?${stringifySearchParams(newSearchParams)}`
            )
        }
    }

    const goToPage = (newPage) => {
        const newOffset = getOffsetForPage(newPage)
        const newPageLimit = getLimitForPage(newPage)
        const isPrepend = newPage < minPageLoaded
        const isAppend = newPage > maxPageLoaded

        // Keep the viewport in place ONLY for a user-initiated prepend chain ("Show
        // Previous"): anchor to the first currently-rendered product element and record its
        // viewport offset; the grid scrolls by the difference after each prepend commit so
        // that element stays visually fixed. An AUTOMATIC group-completion extend-down (e.g.
        // fresh/refresh load of an even page) is NOT anchored — it fills upward and leaves
        // the user at the top. `prependScrollAnchor.current` already set means we're mid-chain
        // (the auto-completed partner page of a user click), so we keep anchoring.
        const isUserPrependChain = userPrependRef.current || prependScrollAnchor.current != null
        if (isPrepend && isUserPrependChain && typeof window !== 'undefined') {
            const anchorId = allProducts?.[0]?.productId
            const el = anchorId ? productRefs.current[anchorId] : null
            prependScrollAnchor.current = el
                ? {anchorId, anchorTop: el.getBoundingClientRect().top}
                : null
        }
        userPrependRef.current = false

        setPage(newPage)
        setOffset(newOffset)
        setIsLoadingNewPage(true)
        // Don't double up prepend skeletons already seeded for this page (even-page landing
        // seeds them ahead of this fetch, in the same commit so state isn't visible yet — a
        // ref is). Each seeded page consumes one prepend extend; the rest create their own.
        if (isPrepend && seededPrependPagesRef.current > 0) {
            seededPrependPagesRef.current -= 1
        } else if (isPrepend) {
            createSkeletonPlaceholders(newPageLimit, 'prepend')
        } else if (isAppend) {
            createSkeletonPlaceholders(newPageLimit, 'append')
        }

        // Only "load more" (append) advances the URL. "Load previous" (prepend, incl. the
        // desktop auto extend-down) loads products above without changing the URL — the
        // URL always tracks the furthest page reached. On desktop the URL only advances
        // once a full group is reached (even page) so it never lands on an odd page; the
        // exception is a partial final group where no higher page exists.
        const isGroupEnd = desktopGroupSize <= 1 || newPage % desktopGroupSize === 0
        const isLastPage = total != null && getOffsetForPage(newPage + 1) >= total
        if (isAppend && (isGroupEnd || isLastPage)) {
            const newParams = new URLSearchParams(location.search)
            newParams.set('page', newPage)
            if (newPage > 1) newParams.set('offset', newOffset)
            else newParams.delete('offset')
            const categoryUrl = getCategoryNavigationPath()
            history.replace(`${categoryUrl}?${newParams.toString()}`)
        }
    }

    // User-initiated "Show Previous": flag the chain as user-driven so the prepend (and the
    // desktop auto-completed partner page) keeps the viewport anchored, and clear any
    // lingering land-at-top intent from a prior back-nav.
    const loadPrevious = () => {
        landAtTopRef.current = false
        userPrependRef.current = true
        if (minPageLoaded > 1) goToPage(minPageLoaded - 1)
    }

    /**************** Product tile onClick handler (for sessionStorage + history) ****************/
    const handleProductClick = ({productSearchItem, productId, index}) => {
        if (searchQuery) {
            sendSelectItemEvent(productSearchItem, {id: 'search_results_page', name: 'search results'}, index)
        } else if (category) {
            setTempRecommendation(category)
            sendSelectItemEvent(productSearchItem, category, index)
        }
        const actualPage = productPageMap.current?.[productId] || page
        const actualOffset = (actualPage - 1) * limit
        sessionStorage.setItem('plp-productContext', JSON.stringify({productId, page: actualPage}))
        const navParams = new URLSearchParams(location.search)
        navParams.set('page', actualPage)
        if (actualPage > 1) navParams.set('offset', actualOffset)
        else navParams.delete('offset')
        history.replace(`${location.pathname}?${navParams.toString()}`)
    }

    // "Show Previous" is visible when there are earlier pages BEFORE the current group —
    // i.e. the group start > 1. On desktop the group start is computed with the stable
    // config group size (so a page-2 landing, whose group starts at page 1, hides the
    // button without a breakpoint flash, while a page-4 landing, group start 3, shows it).
    // On a confirmed mobile view every page is its own group, so it's just minPageLoaded > 1.
    const showLoadPrevious = confirmedMobile
        ? minPageLoaded > 1
        : minPageLoaded - ((minPageLoaded - 1) % initialDesktopGroupSize) > 1

    return {
        // State
        showLoadPrevious,
        sortOpen,
        setSortOpen,
        filtersLoading,
        gridView,
        setGridViewPersisted,
        skeletonPlaceholders,
        allProducts,
        isCategoryChanging,
        page,
        minPageLoaded,
        maxPageLoaded,
        isLoadingNewPage,
        // Search
        isLoading,
        isFetched,
        isRefetching,
        productSearchResult,
        searchQuery,
        isSearch,
        searchParams,
        // Category / CMS
        category,
        categoryContentData,
        isCategoryContentLoading,
        clpPage,
        isClpMode,
        isClpLoading,
        searchNoResultsPage,
        isSearchNoResultsLoading,
        hasSearchNoResultsPage,
        effectiveCategoryId,
        hasSEOUrlParams,
        serverSeoNotFound,
        clientSeoNotFound,
        // Grid
        isAlternativeGridView,
        gridColumns,
        currentBreakpoint,
        isDesktopBreakpoint,
        desktopGroupSize,
        gridInterruptorPositions,
        // CMS sections (PLP)
        aboveProductListBlocks,
        belowProductListBlocks,
        // Derived
        error,
        showNoResults,
        isSmartSearch,
        hasSelectedFilters,
        total,
        sortingOptions,
        sortUrls,
        limitUrls,
        basePath,
        pageTitle,
        limit,
        getLimitForPage,
        // The absolute upper-bound offset of all loaded pages. Using the raw
        // getOffsetForPage(maxPageLoaded + 1) value (rather than subtracting the
        // minPage offset) is necessary so the "Show More" button correctly stops
        // when the loaded pages already cover all products, even after back-
        // navigation where minPageLoaded > 1.
        loadedLimitTotal: getOffsetForPage(maxPageLoaded + 1),
        // Handlers
        toggleFilter,
        resetFilters,
        goToPage,
        loadPrevious,
        handleProductClick,
        addItemToWishlist,
        removeItemFromWishlist,
        // Enrichment / tracking
        enrichProduct,
        observeProduct,
        productRefs,
        productPageMap,
        prependScrollAnchor,
        // Wishlist
        wishlist,
        wishlistLoading,
        // Navigation
        history,
        location,
        onOpen
    }
}
